interface Stage {
  step: number;
  title: string;
  description: string;
  icon: string;
}

const stages: Stage[] = [
  {
    step: 1,
    title: "Discovery Session",
    description: "We learn about your operations, challenges, and goals. A 45-minute call to understand your current state and what success looks like.",
    icon: "ri-search-eye-line",
  },
  {
    step: 2,
    title: "Solution Consultation",
    description: "Our team presents the right StackMap configuration based on your discovery. We map your needs to the right modules and plan tier.",
    icon: "ri-lightbulb-line",
  },
  {
    step: 3,
    title: "Proposal & Agreement",
    description: "You receive a tailored proposal with scope, timeline, and pricing. Once agreed, we kick off onboarding with a dedicated project lead.",
    icon: "ri-file-text-line",
  },
  {
    step: 4,
    title: "Configuration & Setup",
    description: "We configure your StackMap environment, migrate data if needed, and set up integrations. You review and approve before go-live.",
    icon: "ri-settings-4-line",
  },
  {
    step: 5,
    title: "Training & Go-Live",
    description: "Your team gets hands-on training tailored to their roles. We launch in phases to ensure a smooth transition with zero disruption.",
    icon: "ri-rocket-line",
  },
  {
    step: 6,
    title: "Ongoing Support",
    description: "Post-launch, your account manager provides continuous support, quarterly reviews, and access to new features as your business evolves.",
    icon: "ri-shield-star-line",
  },
];

export default function ImplementationJourney() {
  return (
    <section className="relative py-16 md:py-24 bg-graphite-950">
      <div className="section-padding">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Your{" "}
              <span className="text-emerald-accent">Implementation Journey</span>
            </h2>
            <p className="text-base sm:text-lg text-silver-300 max-w-2xl mx-auto leading-relaxed">
              From first conversation to full deployment, here is what working with StackMap looks like.
            </p>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden md:block relative">
            {/* Connecting line */}
            <div className="absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-accent/10 via-emerald-accent/30 to-emerald-accent/10" />

            <div className="grid grid-cols-6 gap-3 lg:gap-4">
              {stages.map((stage, i) => (
                <div key={stage.step} className="relative flex flex-col items-center text-center">
                  {/* Step circle */}
                  <div className="relative z-10 w-12 h-12 flex items-center justify-center rounded-full bg-graphite-900 border-2 border-emerald-accent/40 mb-5">
                    <span className="text-sm font-bold text-emerald-accent">{stage.step}</span>
                  </div>

                  {/* Connector line between circles */}
                  {i < stages.length - 1 && (
                    <div className="absolute top-6 left-[calc(50%+24px)] right-0 h-0.5 bg-emerald-accent/30 -z-0" style={{ width: "calc(100% - 48px)" }} />
                  )}

                  {/* Icon */}
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-accent/10 border border-emerald-accent/20 mb-3">
                    <i className={`${stage.icon} text-emerald-accent text-lg`} />
                  </div>

                  <h4 className="text-sm font-semibold text-white mb-2">{stage.title}</h4>
                  <p className="text-xs text-silver-400 leading-relaxed">{stage.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-accent/10 via-emerald-accent/30 to-emerald-accent/10" />

            <div className="space-y-8">
              {stages.map((stage) => (
                <div key={stage.step} className="relative flex gap-5">
                  {/* Step circle */}
                  <div className="relative z-10 w-12 h-12 flex items-center justify-center shrink-0 rounded-full bg-graphite-900 border-2 border-emerald-accent/40">
                    <span className="text-sm font-bold text-emerald-accent">{stage.step}</span>
                  </div>

                  <div className="flex-1 min-w-0 pt-1.5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-accent/10 border border-emerald-accent/20 mb-3">
                      <i className={`${stage.icon} text-emerald-accent text-lg`} />
                    </div>
                    <h4 className="text-sm font-semibold text-white mb-2">{stage.title}</h4>
                    <p className="text-xs text-silver-400 leading-relaxed">{stage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}